/* eslint-disable no-else-return */
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import createEngine, {
  DefaultPortModel,
  DefaultDiagramState,
  DefaultNodeModel,
  PathFindingLinkFactory,
  LinkModel,
  LinkModelGenerics,
} from "@projectstorm/react-diagrams";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { forEach, debounce, remove } from "lodash";
import { emitCustomEvent } from "react-custom-events";
import * as _ from "lodash";
import { NumberSize } from "re-resizable";
import { Tooltip } from "@material-ui/core";
import DashboardService from "../../../services/dashboard.service";
import Spinner from "../../DesignSystem/spinner";
import Button from "../../DesignSystem/button";
import CanvasBoard from "./CanvasBoard";
import SelectNodeModel from "./CustomNode/SelectNodeModel";
import SelectNodeFactory from "./CustomNode/SelectNodeFactory";
import CustomDeleteItemsAction from "./CustomNode/CustomDeleteItemsAction";
import { NENodeModel } from "./CustomNode/NENodeModel";
import NENodeFactory from "./CustomNode/NENodeFactory";
import {
  getPortIdFromName,
  getPortName,
  getGroupPortName,
  toasterError,
  toasterSuccess, areYouSure,
  toastWarning,
  areYouSureToaster,
  getPortOptions,
  areYouSureOrNot,
  promisedAreYouSure,
  UserChoice
} from "../../../utils/methods";
import { STATUS_TYPE, STATUS_COLOR, ZOOM_CONSTANTS } from "../../../utils/appConstants";
import WarningInfoIcon from "../../../assets/images/warn-toast-icon.svg";
import { ErrorMessages as Errors, SuccessMessage } from "../../../utils/apiConstants";
import ErrorInfoIcon from "../../../assets/images/error-info-icon.svg";
import CustomMouseWheelAction from "./CustomNode/CustomMouseWheelAction";
import CanvasController from "../CanvasController";
import ZoomFunction from "../CanvasController/ZoomFunction";
import BandwidthLinkFactory from "./CustomLink/BandwidthLink/BandwidthLinkFactory";
import BandwidthLinkModel from "./CustomLink/BandwidthLink/BandwidthLinkModel";
import CrossConnectLinkFactory from "./CustomLink/CrossConnectLink/CrossConnectLinkFactory";
import CrossConnectLinkModel from "./CustomLink/CrossConnectLink/CrossConnectLinkModel";
import DiagramModel from "./WVDiagramModel";
import DefaultLinkModel from "./CustomLink/DefaultLink/DefaultLinkModel";
import DefaultLinkFactory from "./CustomLink/DefaultLink/DefaultLinkFactory";
import DefaultLabelFactory from "./CustomLabel/DefaultLabelFactory";
import { mode } from "cesium";
import { useIsMounted } from "../../../utils/customHooks";
import { dir, group } from "console";
import MenuSection from "../MenuSection";
import CrossConnectService from "../../../services/crossConnect.service";
import { CrossConnect } from "../../../interfaces/crossConnect.interface";

interface CanvasProps {
  selectedNetworkElements: any;
  ports: any;
  canvasData?: any; /* WARNING WORKING WITH CANVAS DATA!!!!!!!!!!!!
  The canvas data prop relies on the updateCanvasDataToAPI function. 
  The updateCanvasDataToAPI function is called only after two seconds of user inactivity. 
  Therefore, be careful using canvasData on freshly placed nodes (when nodes are added on to the canvas via the drawer) 
  because the new nodes canvasData property will be null.
  For example, in the geographicalLayout function, the node id was initially pulled using canvasData, 
  however, after realizing that newly added nodes were returning a null value for canvasData,
  selectedNe was used instead. 
  Be aware of canvasData since at times the object is null. */
  portsLoading: boolean;
  onNeOpen: () => void;
  scale: any;
  refetch: () => void;
  onDeleteClick: (_: any) => void;
  onRefetch: () => void;
  // dirtyData: {
  //   create: any[];
  //   update: any[];
  //   delete: any[];
  // };
  // setDirtyData: (_: any) => void;
  crossConnectedPortIdsDB: any[];
  parentExpandedGroupIds: any[];
  setParentExpandedGroupIds: (_: any[]) => void;
  setCrossConnectedPortIdsDB: (_: any) => void;
  isMounted: boolean;
  // menuProps: any;
  setCanvasData: (_: any) => void;
  //isDirty: boolean;
  // handleSaveReset: (_: any) => void;
}
// create an engine without registering DeleteItemsAction
const engine = createEngine({ registerDefaultDeleteItemsAction: false, registerDefaultZoomCanvasAction: false });
const state = engine.getStateMachine().getCurrentState();
if (state instanceof DefaultDiagramState) {
  state.dragNewLink.config.allowLooseLinks = false;
}
// register an DeleteItemsAction with custom keyCodes (in this case, only Delete key)
engine.getActionEventBus().registerAction(new CustomMouseWheelAction());
engine.getActionEventBus().registerAction(new CustomDeleteItemsAction());
engine.getNodeFactories().registerFactory(new SelectNodeFactory());
engine.getNodeFactories().registerFactory(new NENodeFactory());
engine.getLinkFactories().registerFactory(new BandwidthLinkFactory());
engine.getLinkFactories().registerFactory(new CrossConnectLinkFactory());
engine.getLinkFactories().registerFactory(new DefaultLinkFactory());
// @ts-ignore
engine.getLabelFactories().registerFactory(new DefaultLabelFactory());
const model = new DiagramModel();
let addDirtyData: (operation: "add" | "delete", port_id: number, other_port_id: number, code: number, linkCopy: LinkModel) => void;
let currentSelectedLink: any;
// Register the model event
model.registerListener({
  linksUpdated: (e: any) => {
    if (e.isCreated && e.link) {
      if (!(e.link instanceof CrossConnectLinkModel) && !(e.link instanceof BandwidthLinkModel)) {
        e.link.registerListener({
          targetPortChanged: (e: any) => {
            e.entity.getOptions().isDashed = true;
            e.entity.getOptions().selected = false;
            try {
              const link = e.entity;
              const linkCopy = _.clone(link);
              if (link) {
                const removeLink = () => {
                  linkCopy.remove();
                  engine.repaintCanvas();
                };
                const source = link.getSourcePort() as DefaultPortModel;
                const destination = link.getTargetPort() as DefaultPortModel;
                const sourceOptions = getPortOptions(link.getSourcePort() as DefaultPortModel);
                const destinationOptions = getPortOptions(link.getTargetPort() as DefaultPortModel);
                if (source && destination && sourceOptions && destinationOptions) {
                  const destinationLinks = [...Object.values(destination.getLinks())];
                  const numberDestinationLinks = destinationLinks
                    .map((lnk: LinkModel<LinkModelGenerics>) => {
                      if (!(lnk instanceof BandwidthLinkModel)) {
                        return lnk.getRenderedPath().length;
                      }
                      return 0;
                    })
                    .reduce((a: number, b: number) => a + b);
                  const sourceLinks = [...Object.values(source.getLinks())];
                  const numberSourceLinks = sourceLinks
                    .map((lnk: LinkModel<LinkModelGenerics>) => {
                      if (!(lnk instanceof BandwidthLinkModel)) {
                        return lnk.getRenderedPath().length;
                      }
                      return 0;
                    })
                    .reduce((a: number, b: number) => a + b);
                  if (numberDestinationLinks > 1 || numberSourceLinks > 1) {
                    toasterError("Invalid operation. Cannot have multiple cross connects from one port.");
                    removeLink();
                  } else {
                    if (!sourceOptions || !destinationOptions) return;
                    
                    // WV-2453 Action Costs Money
                    // I suggest modifying the getCrossConnectActionRequired API call to send the amount of money and display it here.
                    // Sometimes making a cross connect will not cost money, for example if its WX to WX port, however in most cases it will.

                    DashboardService.getCrossConnectActionRequired({ nrId1: sourceOptions.portId, nrId2: destinationOptions.portId })
                      .then((res) => {
                        if (res.data) {
                          switch (res.data.toaster) {
                            case 1:
                              addDirtyData("add", sourceOptions.portId, destinationOptions.portId, res.data.code, linkCopy);
                              break;
                            case 2:
                              toastWarning(res.data.message);
                              addDirtyData("add", sourceOptions.portId, destinationOptions.portId, res.data.code, linkCopy);
                              break;
                            case 3:
                              areYouSureOrNot(
                                () => addDirtyData("add", sourceOptions.portId, destinationOptions.portId, res.data.code, linkCopy),
                                removeLink,
                                res.data.message
                              );
                              break;
                          }
                        }
                      })
                      .catch((e) => console.error(e));
                  }
                } else {
                  throw "Ports do not exist";
                }
              }
            } catch (e) {
              // console.log(e);
            }

            // setUpdateUnSavedData("create", "link", _.clone(e.entity));
          },
        });
      }
      if (!(e.link instanceof BandwidthLinkModel)) {
        e.link.registerListener({
          entityRemoved: (e: any) => {
            // If drawn and immediately deleted, the entity removed will have no target. If deleted normally, it will be undefined
            if (e.entity.targetPort === null) return;
            if (currentSelectedLink) {
              const sourceOptions = getPortOptions(currentSelectedLink.getSourcePort() as DefaultPortModel);
              const destinationOptions = getPortOptions(currentSelectedLink.getTargetPort() as DefaultPortModel);
              if (!sourceOptions || !destinationOptions) return;
              addDirtyData("delete", sourceOptions.portId, destinationOptions.portId, 0, currentSelectedLink);
              engine.repaintCanvas();
            }
          },
        });
        e.link.registerListener({
          selectionChanged: (e: any) => {
            currentSelectedLink = _.cloneDeep(e.entity);
          },
        });
      }
    }
  },
  offsetUpdated: () => {
    engine && engine.fireEvent({}, "onCanvasMove");
  },
  zoomUpdated: () => {
    engine && engine.fireEvent({}, "onCanvasMove");
  },
});

const Canvas: React.FC<CanvasProps> = ({
  selectedNetworkElements,
  canvasData,
  scale,
  ports,
  refetch: refetchAll,
  portsLoading,
  parentExpandedGroupIds,
  setParentExpandedGroupIds,
  onRefetch,
  onNeOpen,
  // menuProps,
  onDeleteClick,
  // dirtyData,
  // setDirtyData,
  crossConnectedPortIdsDB,
  setCrossConnectedPortIdsDB,
  setCanvasData,
  // isDirty,
  // handleSaveReset,
}) => {
  const isMounted = useIsMounted();
  const [dirtyData, setDirtyData] = useState<CrossConnect[]>([]);
  const [isZoomDisbaled, setIsZoomDisabled] = useState(false);

  addDirtyData = useCallback(
    (operation: "add" | "delete", port_id: number, other_port_id: number, code: number, linkCopy: LinkModel) => {
      const maxPortId = Math.max(port_id, other_port_id);
      const minPortId = Math.min(port_id, other_port_id);
      const existingIndex = dirtyData.findIndex((row) => {
        const minEqual = row.min_port_id === minPortId;
        const maxEqual = row.max_port_id === maxPortId;
        return minEqual && maxEqual;
      });
      const existing = existingIndex >= 0 && dirtyData[existingIndex];
      // ADD CASE
      if (operation === "add") {
        if (existing) {
          // If there already exists an add, throw error
          if (existing.operation === "add") {
            toasterError("Cross connect already drawn.");
            return;
            // If there exists a delete, remove the delete
          } else if (existing.operation === "delete") {
            const updatedDirtyData = [...dirtyData.slice(0, existingIndex), ...dirtyData.slice(existingIndex + 1)];
            setDirtyData(updatedDirtyData);
          } else {
            console.error("Unexpected");
          }
        } else {
          //
          setDirtyData((prev) => [...prev, { operation, min_port_id: minPortId, max_port_id: maxPortId, code, linkCopy }]);
        }
        // DELETE CASE
      } else if (operation === "delete") {
        if (existing) {
          // If there already exists an add, throw error
          if (existing.operation === "delete") {
            toasterError("Cross connect already deleted.");
            return;
            // If there exists a delete, remove the delete
          } else if (existing.operation === "add") {
            const updatedDirtyData = [...dirtyData.slice(0, existingIndex), ...dirtyData.slice(existingIndex + 1)];
            setDirtyData(updatedDirtyData);
          } else {
            console.error("Unexpected");
          }
        } else {
          //
          setDirtyData((prev) => [...prev, { operation, min_port_id: minPortId, max_port_id: maxPortId, code, linkCopy }]);
        }
      }
    },
    [dirtyData]
  );
  const handleSave = useCallback(() => {
    CrossConnectService.crossConnect({
      data: dirtyData.map((ele: CrossConnect) => {
        return { max_port_id: ele.max_port_id, min_port_id: ele.min_port_id, operation: ele.operation, code: ele.code };
      }),
    })
      .then((response: any) => {
        if (!isMounted) return;
        if (response.data.connectResult.failCount + response.data.disConnectresult.failCount > 0) {
          toasterError("Some cross connect actions could not be made");
        } else {
          toasterSuccess(SuccessMessage.crossConnectBulkSuccess);
        }
        setDirtyData([]);
        refetchAll();
      })
      .catch(() => {
        toasterError(SuccessMessage.crossConnectBulkFailed);
      });
  }, [dirtyData]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<any[]>([]);
  const [saveCanvasPositionLoading, setSaveCanvasPositionLoading] = useState<boolean>(false);
  /*useEffect(() => {
    if (parentExpandedGroupIds.length === 0) {
      // setExpandedGroupIds([]); //DOES THIS BREAK ANYTHING?
      // console.log('empited')
      // localStorage.setItem("expandedGroupIds", JSON.stringify([]));
    }
  }, [parentExpandedGroupIds]);*/
  const ref = useRef(null as HTMLDivElement | null);

  const updateCanvasDataToAPI = useCallback((saveCanvas: boolean = false) => {
    if (saveCanvasPositionLoading || !isMounted) return;
    setSaveCanvasPositionLoading(true);
    if (!model) return;
    const zoomLevel = model.getZoomLevel() || 80;
    const offsetLevelX = model.getOffsetX();
    const offsetLevelY = model.getOffsetY();
    const nodeList: any[] = [];
    forEach(model.getNodes(), (node: any) => {
      const { selectedNe, portsList, portsUsed, groups, groupsUsed } = node;
      const portList: any[] = [];
      if (portsList) {
        portsList.forEach((port: any) => {
          const portItem = {
            portId: port.portId,
            x: port.x || 0,
            y: port.y || 0,
          };
          portList.push(portItem);
        });
      }
      const portsUsedList: any[] = [];
      if (portsUsed) {
        portsUsed.forEach((port: any) => {
          const portItem = {
            portId: port.portId,
            x: port.x || 0,
            y: port.y || 0,
          };
          portsUsedList.push(portItem);
        });
      }
      const grpList: any[] = [];
      if (groups) {
        groups.forEach((grp: any) => {
          const grpItem = {
            groupId: grp.groupId,
            groupName: grp.groupName,
            x: grp.x || 0,
            y: grp.y || 0,
          };
          grpList.push(grpItem);
        });
      }
      const grpUsedList: any[] = [];
      if (groupsUsed) {
        groupsUsed.forEach((grp: any) => {
          const grpItem = {
            groupId: grp.groupId,
            x: grp.x || 0,
            y: grp.y || 0,
          };
          grpUsedList.push(grpItem);
        });
      }
      if (selectedNe) {
        const nodeItem = {
          id: selectedNe.id,
          node_id: selectedNe.node_id,
          ne_id: selectedNe.ne_id,
          city: selectedNe.city,
          ne_name: selectedNe.ne_name,
          position: node.position,
          width: node.width,
          height: node.height,
          portList,
          portsUsedList,
          grpList,
          grpUsedList,
        };
        nodeList.push(nodeItem);
      }
    });

    const jsonData = {
      zoom: zoomLevel,
      offsetX: offsetLevelX,
      offsetY: offsetLevelY,
      nodes: nodeList,
    };

    const saveCanvasPosition = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (saveCanvas) {
          DashboardService.saveUserCanvasPosition(jsonData)
            .then(() => {
              if (!isMounted) return;
              setSaveCanvasPositionLoading(false);
              toasterSuccess("Canvas position saved successfully");
              resolve();
            })
            .catch(() => {
              if (!isMounted) return;
              toasterError("Saving canvas position unsuccessful");
              setSaveCanvasPositionLoading(false);
              reject();
            });
            localStorage.setItem('hasSavedCanvasPosition', 'true');
        } else {
          resolve();
        }
      });
    };
    if (!isMounted) return;
    saveCanvasPosition()
      .then(() => {
        if (!saveCanvas) {
          DashboardService.putCanvasData(jsonData)
            .then(() => {
              if (!isMounted) return;
              setSaveCanvasPositionLoading(false);
            })
            .catch(() => {
              if (!isMounted) return;
              toasterError("Saving canvas position unsuccessful");
              setSaveCanvasPositionLoading(false);
            });
        }
      });
  }, [isMounted, saveCanvasPositionLoading, model]);

  const handleUpdateCanvasToAPI = useMemo(
    () =>
      debounce(() => {
        updateCanvasDataToAPI();
      }, 2000),
    []
  );

  const [showData, setShowData] = useState(false);
  const [addNodeListner, setAddNodeListner] = useState<any>();
  const [refetchListener, setRefetchListener] = useState<any>();
  const [deleteNodeListner, setDeleteNodeListner] = useState<any>();
  const [refetchListner, setRefetchListner] = useState<any>();
  const [expandedGroupIdListner, setExpandedGroupIdListner] = useState<any>();
  const [saveCanvasListener, setSaveCanvasListener] = useState<any>();

  const getLinkedPort = (nodeId: any, portId: any): any => {
    const model = engine.getModel();
    const portName = getPortName(portId);
    let resultPort;
    forEach(model.getNodes(), (node: any) => {
      const { selectedNe } = node;
      if (selectedNe && selectedNe.id == nodeId) {
        forEach(node.getPorts(), (port: any) => {
          if (port && port.getOptions().name == portName) {
            resultPort = port;
          }
        });
      }
    });
    return resultPort;
  };

  const getLinkColor = (bandWidth: any, organizationId: number) => {
    if (bandWidth.status === STATUS_TYPE.FOR_SALE_BW && bandWidth.organization_id != organizationId) {
      return STATUS_COLOR.FOR_SELL_OTHERS;
    }
    if (bandWidth.status === STATUS_TYPE.FOR_SALE_BW) {
      return STATUS_COLOR.FOR_SELL;
    }
    if (bandWidth.status === STATUS_TYPE.BOUGHT_BW && bandWidth.current_owner_organization_id != organizationId) {
      return STATUS_COLOR.SOLD;
    }
    if (!bandWidth.bandwidth_verified) {
      return STATUS_COLOR.UN_VERIFY;
    }
    return STATUS_COLOR.VERIFY;
  };

  const getHigherColor = (colorOne: string, colorTwo: string): string => {
    if (colorOne === STATUS_COLOR.VERIFY || colorTwo === STATUS_COLOR.VERIFY) {
      return STATUS_COLOR.VERIFY;
    } else if (colorOne === STATUS_COLOR.UN_VERIFY || colorTwo === STATUS_COLOR.UN_VERIFY) {
      return STATUS_COLOR.UN_VERIFY;
    } else if (colorOne === STATUS_COLOR.FOR_SELL || colorTwo === STATUS_COLOR.FOR_SELL) {
      return STATUS_COLOR.FOR_SELL;
    } else if (colorOne === STATUS_COLOR.FOR_SELL_OTHERS || colorTwo === STATUS_COLOR.FOR_SELL_OTHERS) {
      return STATUS_COLOR.FOR_SELL_OTHERS;
    } else if (colorOne === STATUS_COLOR.SOLD || colorTwo === STATUS_COLOR.SOLD) {
      return STATUS_COLOR.SOLD;
    }
    return STATUS_COLOR.VERIFY;
  };

  const removeExistingLinks = useCallback(() => {
    if (engine && engine.getModel()) {
      forEach(engine.getModel().getLinks(), (link) => {
        engine.getModel().removeLink(link);
      });
    }
  }, [engine]);

  const getTooltipElement = (tooltip: string[], bandwidthName: string): any => {
    if (tooltip.length <= 1) {
      return `${tooltip[0]} ${bandwidthName}`;
    } else {
      const map = tooltip.reduce((acc, e) => acc.set(e, (acc.get(e) || 0) + 1), new Map());
      const mapArray = Array.from(map.entries());
      let count = 0;
      const listElements = [];
      while (count < 5) {
        count++;
        let bestTuple = ["", 0];
        let removeIndex = 0;
        mapArray.forEach((array: any[], index: number) => {
          if (array && bestTuple[1] < array[1]) {
            bestTuple = array;
            removeIndex = index;
          }
        });
        if (bestTuple[1] === 0) {
          break;
        } else if (bestTuple[1] === 1) {
          listElements.push(<li key={count}>{bestTuple[0]}</li>);
        } else {
          listElements.push(<li key={count}>{`${bestTuple[0]} (${bestTuple[1]})`}</li>);
        }
        delete mapArray[removeIndex];
      }
      if (count >= 5) {
        let extraCount = 0;
        mapArray.forEach((array: any[]) => {
          extraCount += array[1];
        });
        if (extraCount > 0) {
          listElements.push(<li key={++count}>{`+${extraCount}`}</li>);
        }
      }
      return <ul>{listElements}</ul>;
    }
  };

  const renderCrossConnects = useCallback(
    (calculatedExpandedGroupIds: any[], groupCountMap: Map<string, number>) => {
      if (!ports || !ports.cross_connects) return;
      ports.cross_connects.forEach((cc: any) => {
        const sourcePort = getLinkedPort(cc.ne_id, cc.port1_id);
        const targetPort = getLinkedPort(cc.ne_id, cc.port2_id);
        if (sourcePort && targetPort) {
          const sourcePortObj = sourcePort as DefaultPortModel;
          const targetPortObj = targetPort as DefaultPortModel;
          const sourceId = sourcePortObj.getOptions().name;
          const targetId = targetPortObj.getOptions().name;
          const sourceOptions = getPortOptions(sourcePortObj);
          const targetOptions = getPortOptions(targetPortObj);

          const link = new CrossConnectLinkModel();
          link.setSourcePort(sourcePort);
          link.setTargetPort(targetPort);

          if (
            cc.on_net &&
            (sourceOptions.verified || sourceOptions.requested_verification) &&
            (targetOptions.verified || targetOptions.requested_verification)
          ) {
            link.setColor(STATUS_COLOR.VERIFY);
          } else {
            link.setColor(STATUS_COLOR.UN_VERIFY);
          }

          if (cc.port1_cc_pending || cc.port2_cc_pending) {
            link.getOptions().isDashed = true;
          }

          const sourceCollapsed = cc.port1_group && !calculatedExpandedGroupIds.includes(Number(cc.port1_group));
          const targetCollapsed = cc.port2_group && !calculatedExpandedGroupIds.includes(Number(cc.port2_group));
          const minGroup = Math.min(Number(cc.port1_group), Number(cc.port2_group));
          const maxGroup = Math.max(Number(cc.port1_group), Number(cc.port2_group));
          const multipleCrossConnectsBetweenGroups = cc.port1_group && cc.port2_group && (groupCountMap.get(minGroup + "-" + maxGroup) || 0) > 1;
          const isSameGroup = cc.port1_group === cc.port2_group;
          if (isSameGroup && sourceCollapsed && targetCollapsed) {
            link.setCurvyness(0);
          } else if (isSameGroup) {
            link.setCurvyness(50);
          }

          link.getOptions().extras = {
            isBandwidthLink: false,
            uniqueId: `S${sourceId}T${targetId}`,
            sourceCollapsed,
            targetCollapsed,
            multipleCrossConnectsBetweenGroups,
          };

          engine.getModel().addLink(link);
        }
      });
    },
    [setCrossConnectedPortIdsDB, crossConnectedPortIdsDB, expandedGroupIds, ports, removeExistingLinks, getLinkedPort, getLinkColor]
  );

  const [linksLoading, setLinksLoading] = useState(false);
  const setPortLinks = useCallback(
    (defaultExpanded?: any[]) => {
      if (!isMounted) return;
      setLinksLoading(true);
      const calculatedExpandedGroupIds: any[] = defaultExpanded || expandedGroupIds;
      // case: link is drawn between two ports both in collapsed groups -> only draw one link per group-pair, make a tooltip with all bd names
      const groupMap = new Map<number, number>(); // to map collapsed group to collapsed group
      // const collapsedGroupToPort = new Map<number, DefaultPortModel>(); // to map collapsed group to their port, all CC below will be drawn to this port
      type linkColorTooltipType = { color: string; tooltip: string[] };
      type linkDetailsType = {
        name: string;
        first?: boolean;
        groups: string;
        source: DefaultPortModel;
        target: DefaultPortModel;
        details: linkColorTooltipType;
      };
      const endpoints: linkDetailsType[] = [];
      removeExistingLinks();

      // Count Ports in Each group
      const groupCountMap = new Map<string, number>();
      if (ports && ports.nodes) {
        try {
          ports.nodes.forEach((node: any) => {
            const arr = [];
            if (node) {
              if (node.groups) arr.push(...node.groups);
              if (node.bandwidth_groups_used) arr.push(...node.bandwidth_groups_used);
            }

            arr.forEach((group: any) => {
              if (!group || !group.ports) return;
              const portsWithCrossConnects = group.ports.filter((port: any) => !!port.crossConnectPortId);
              portsWithCrossConnects.forEach((port: any) => {
                const minGroup = Math.min(Number(port.groupId), Number(port.crossConnectGroupId));
                const maxGroup = Math.max(Number(port.groupId), Number(port.crossConnectGroupId));
                const current = groupCountMap.get(minGroup + "-" + maxGroup);
                if (!current) groupCountMap.set(minGroup + "-" + maxGroup, 0.5);
                else groupCountMap.set(minGroup + "-" + maxGroup, current + 0.5);
              });
            });
          });
        } catch (e) {
          console.error(e);
        }
      }

      /*
        make the foreach loop end before the @source&&target)
        push all source target pairs onto an array
        get group tooltip and color down below
        after all that is done, then make cc/dirtydata only connect to groups using groupToPort index
      */
      const organizationId = parseInt(localStorage.getItem("org_id") || "0");
      if (ports && ports.bandwidths) {
        ports.bandwidths.forEach((bandWidth: any) => {
          let sourcePort: DefaultPortModel;
          let targetPort: DefaultPortModel;
          // if both nearend and farend are part collapsed group, and the collapsed group pair has already been explored
          if (
            bandWidth.n_group_id &&
            !calculatedExpandedGroupIds.includes(bandWidth.n_group_id) &&
            bandWidth.f_group_id &&
            !calculatedExpandedGroupIds.includes(bandWidth.f_group_id) &&
            (groupMap.get(bandWidth.n_group_id) === bandWidth.f_group_id || groupMap.get(bandWidth.f_group_id) === bandWidth.n_group_id)
          ) {
            sourcePort = getLinkedPort(bandWidth.nne_id, bandWidth.nne_portId);
            targetPort = getLinkedPort(bandWidth.fne_id, bandWidth.fne_portId);
            // append the collapsed group endpoints with the color and link tooltip
            endpoints.forEach((linkDetails: linkDetailsType) => {
              if (
                linkDetails.first &&
                (linkDetails.groups === `${bandWidth.n_group_id}-${bandWidth.f_group_id}` ||
                  linkDetails.groups === `${bandWidth.f_group_id}-${bandWidth.n_group_id}`)
              ) {
                const { details } = linkDetails;
                details.color = getHigherColor(getLinkColor(bandWidth, organizationId), details.color);
                details.tooltip.push(bandWidth.description);

                endpoints.push({
                  first: false,
                  groups: linkDetails.groups,
                  source: sourcePort,
                  target: targetPort,
                  details,
                  name: bandWidth.bandwidth_name || "",
                });
              }
            });
          } else {
            sourcePort = getLinkedPort(bandWidth.nne_id, bandWidth.nne_portId);
            targetPort = getLinkedPort(bandWidth.fne_id, bandWidth.fne_portId);
            const tooltip = [bandWidth.description];
            const color = getLinkColor(bandWidth, organizationId);
            endpoints.push({
              first: true,
              groups: `${bandWidth.n_group_id}-${bandWidth.f_group_id}`,
              source: sourcePort,
              target: targetPort,
              details: { color, tooltip: [...tooltip] },
              name: bandWidth.bandwidth_name || "",
            });
            if (bandWidth.n_group_id && bandWidth.f_group_id) {
              groupMap.set(bandWidth.n_group_id, bandWidth.f_group_id);
            }
          }
        });
        endpoints.forEach((linkDetails: linkDetailsType) => {
          if (linkDetails.source && linkDetails.target) {
            const link = new BandwidthLinkModel();
            link.setSourcePort(linkDetails.source); // source port is nearend
            link.setTargetPort(linkDetails.target); // target port is farend
            link.getOptions().tooltipItem = <div className="bandwidth-tooltip">{getTooltipElement(linkDetails.details.tooltip, linkDetails.name)}</div>;
            link.setColor(linkDetails.details.color);
            const n_port = linkDetails.source.getOptions().extras.data;
            const f_port = linkDetails.target.getOptions().extras.data;
            const sourceGroup = organizationId === n_port.organizationId ? n_port.groupId || n_port.group_id : n_port.purchaseGroupId || n_port.group_id;
            const targetGroup = organizationId === f_port.organizationId ? f_port.groupId || f_port.group_id : f_port.purchaseGroupId || f_port.group_id;
            const sourceCollapsed = sourceGroup && !calculatedExpandedGroupIds.includes(sourceGroup);
            const targetCollapsed = targetGroup && !calculatedExpandedGroupIds.includes(targetGroup);
            const minGroup = Math.min(Number(sourceGroup), Number(targetGroup));
            const maxGroup = Math.max(Number(sourceGroup), Number(targetGroup));
            const multipleCrossConnectsBetweenGroups = sourceGroup && targetGroup && (groupCountMap.get(minGroup + "-" + maxGroup) || 0) > 1;
            link.getOptions().extras = {
              isBandwidthLink: true,
              sourceCollapsed,
              targetCollapsed,
              first: linkDetails.first,
              multipleCrossConnectsBetweenGroups,
            };
            if (linkDetails.details.color !== STATUS_COLOR.FOR_SELL_OTHERS) engine.getModel().addLink(link); // REMOVE WHITE LINKS
          }
        });
      }

      renderCrossConnects(calculatedExpandedGroupIds, groupCountMap);

      // Render drawn links
      dirtyData.forEach((ele) => {
        if (ele.operation === "add") {
          engine.getModel().addLink(ele.linkCopy);
        } else if (ele.operation === "delete") {
          const link = ele.linkCopy as DefaultLinkModel;
          const linkUniqueId = link.getOptions().extras;

          if (engine && engine.getModel()) {
            forEach(engine.getModel().getLinks(), (link1) => {
              const link1UniqueId = link1.getOptions().extras;
              if (linkUniqueId && link1UniqueId && linkUniqueId.uniqueId === link1UniqueId.uniqueId) {
                engine.getModel().removeLink(link1);
              }
            });
          }
        }
      });

      setLinksLoading(false);
    },
    [setCrossConnectedPortIdsDB, crossConnectedPortIdsDB, expandedGroupIds, ports, removeExistingLinks, getLinkedPort, getLinkColor]
  );

  const onGroupAction = useCallback((event: any) => {
    const { action, groupId } = event;
    let tempGroupIds: any[] = [];
    if (action === "remove") {
      if (!isMounted) return;
      setExpandedGroupIds((existing: any[]) => {
        let savedExpandedGroups = JSON.parse(localStorage.getItem("expandedGroupIds") || "[]");
        savedExpandedGroups = savedExpandedGroups.filter((item: any) => item !== groupId);
        localStorage.setItem("expandedGroupIds", JSON.stringify(savedExpandedGroups));
        tempGroupIds = existing.filter((item) => item !== groupId);
        return existing.filter((item) => item !== groupId);
      });
    } else {
      if (!isMounted) return;
      setExpandedGroupIds((existing: any[]) => {
        tempGroupIds = [...existing, groupId];
        let savedExpandedGroups = JSON.parse(localStorage.getItem("expandedGroupIds") || "[]");

        if (!savedExpandedGroups.includes(groupId)) {
          savedExpandedGroups.push(groupId);
          localStorage.setItem("expandedGroupIds", JSON.stringify(savedExpandedGroups));
        }
        return [...existing, groupId];
      });
    }
    if (!isMounted) return;
    setParentExpandedGroupIds(tempGroupIds);
  }, []);

  useEffect(() => {
    setPortLinks();
    engine.repaintCanvas();
  }, [expandedGroupIds]);

  const removeAllNodes = () => {
    if (engine && engine.getModel() && engine.getModel().getNodes().length) {
      const currentNodes = engine.getModel().getNodes();
      currentNodes.forEach((nodeItem: any) => {
        engine.getModel().removeNode(nodeItem);
      });
    }
  };

  const removeListeners = () => {
    engine.deregisterListener(addNodeListner);
    engine.deregisterListener(deleteNodeListner);
    engine.deregisterListener(refetchListner);
    engine.deregisterListener(expandedGroupIdListner);
    engine.deregisterListener(refetchListener);
    engine.deregisterListener(saveCanvasListener);
    engine &&
      engine
        .getModel()
        .getNodes()
        .forEach((item) => {
          item.clearListeners();
        });
  };

  const intersectRect = (r1: any, r2: any) => {
    return !(r2.left > r1.right || r2.right < r1.left || r2.top > r1.bottom || r2.bottom < r1.top);
  };

  const isIntersect = (draggedNodeRect: any, nodeList: any) => {
    const flagList: any[] = [];
    forEach(nodeList, (node: any) => {
      const { width, height, position } = node;
      const nodeRect = {
        left: position.x,
        top: position.y,
        right: position.x + width,
        bottom: position.y + height,
      };
      const result = intersectRect(draggedNodeRect, nodeRect);
      let newRect = {};
      if (result) {
        newRect = {
          left: position.x - (draggedNodeRect.width + 25),
          top: position.y,
          right: position.x - 1,
          bottom: position.y + draggedNodeRect.height,
          width: draggedNodeRect.width,
          height: draggedNodeRect.height,
        };
      }
      flagList.push({
        newRect,
        result,
      });
    });
    return flagList;
  };

  const checkPositionAvailablity = (draggedNodeRect: any, nodeList: any): any => {
    const flagList = isIntersect(draggedNodeRect, nodeList);
    const intersected = flagList.filter((f: any) => f.result === true);
    if (intersected.length > 0) {
      const result = intersected.reduce((res: any, obj: any) => {
        return obj.newRect.left < res.newRect.left ? obj : res;
      });
      return checkPositionAvailablity(result.newRect, nodeList);
    } else {
      return draggedNodeRect;
    }
  };

  const addListners = () => {
    if (engine) {
      removeListeners();
      const addNewNodeListner = engine.registerListener({
        onAddNewNodeClick: (event) => onNeOpen(),
      });
      setAddNodeListner(addNewNodeListner);
      const deleteListner = engine.registerListener({
        onDeleteClick: (event) => onDeleteClick(event),
      });
      setDeleteNodeListner(deleteListner);
      const fetchListner = engine.registerListener({
        onRefetchClick: (event) => onRefetch(),
      });
      setRefetchListner(fetchListner);
      const refetchListener = engine.registerListener({
        refetchListener: () => refetchAll(),
      });
      setRefetchListener(refetchListener);
      const expandedGroupIdListner = engine.registerListener({
        onGroupAction: (event) => onGroupAction(event),
      });
      setExpandedGroupIdListner(expandedGroupIdListner);
      const saveCanvasListener = engine.registerListener({
        onCanvasMove: () => handleUpdateCanvasToAPI(),
      });
      setSaveCanvasListener(saveCanvasListener);
    }
  };

  useEffect(() => {
    model.setZoomLevel(scale);
    engine.repaintCanvas();
  }, [scale]);

  const PortsUpdated = useCallback(() => {
    if (ports) {
      removeAllNodes();
      if (!ports.nodes || ports.nodes.length === 0) {
        const node1 = new SelectNodeModel();
        node1.setPosition(50, 130);
        model.addAll(node1);
      } else {
        const nodeData: any = [];
        let newlyAddedNodeCount = 0;
        const NODE_IN_EACH_ROW = 4;
        ports.nodes.forEach((node: any, index: number) => {
          if (node && node.ports) {
            const nodeItem = canvasData && canvasData.nodes ? canvasData.nodes.find((n: any) => n.id === node.id) : null;
            const portList = node.ports.length ? node.ports[0] : [];
            const portUsedAll = node.bandwidth_ports_used || [];
            const groupList = node.groups || [];
            const groupUsedAll = node.bandwidth_groups_used || [];

            const tempNode = new NENodeModel({
              portsList: portList,
              groups: groupList,
              selectedNe: node,
              portsUsed: portUsedAll,
              groupsUsed: groupUsedAll,
              bandwidths: ports.bandwidths || [],
              nodeDataCanvas: nodeItem,
            });
            if (canvasData) {
              if (nodeItem) {
                const { position } = nodeItem;
                tempNode.setPosition(position.x, position.y);
              } else {
                // this node is newly added, show it on top, left corner
                // do - canvasData.offsetX to put at proper location
                tempNode.setPosition((newlyAddedNodeCount + 1) * 45 + 5 - canvasData.offsetX, (newlyAddedNodeCount + 1) * 85 - 5 - canvasData.offsetY);
                newlyAddedNodeCount++;
              }
            } else {
              const rowNo = Math.floor(index / NODE_IN_EACH_ROW);
              tempNode.setPosition((index + 1) * 450 + 50 - rowNo * (NODE_IN_EACH_ROW * 450), rowNo * 660 + 130);
            }
            nodeData.push(tempNode);
          }
        });
        nodeData.forEach((element: any) => {
          model.addNode(element);
        });
      }
      addListners();
      model.setZoomLevel(scale);
      if (canvasData) {
        model.setOffset(canvasData.offsetX, canvasData.offsetY);
      }
    } else {
      setShowData(true);
    }
    if (!engine.getModel()) {
      engine.maxNumberPointsPerLink = 0;
      // @ts-ignore
      engine.setModel(model);
      engine.repaintCanvas();
    } else {
      setPortLinks();
    }
  }, [ports, canvasData, selectedNetworkElements]);

  const delapNodes = useCallback(() => {
    const nodes = model.getNodes();
    if (nodes.length < 2) return;
    let hasOverlap = true;
    const MAX_ITERATIONS = 100; 
    let iterations = 0;
    const SPACING = 50; 
    while (hasOverlap && iterations < MAX_ITERATIONS) {
      hasOverlap = false;
      iterations++;
      const nodePositions = nodes.map(node => ({
        id: node.getID(),
        node: node,
        x: node.getPosition().x,
        y: node.getPosition().y,
        width: node.width,
        height: node.height + 82
      })); 
      for (let i = 0; i < nodePositions.length; i++) {
        for (let j = i + 1; j < nodePositions.length; j++) {
          const node1 = nodePositions[i];
          const node2 = nodePositions[j];
          const isOverLapping = !(
            node1.x + node1.width + SPACING < node2.x ||
            node2.x + node2.width + SPACING < node1.x ||
            node1.y + node1.height + SPACING < node2.y ||
            node2.y + node2.height + SPACING < node1.y
          );
  
          if (isOverLapping) {
            hasOverlap = true;
            const center1 = {
              x: node1.x + node1.width / 2,
              y: node1.y + node1.height / 2
            };
            const center2 = {
              x: node2.x + node2.width / 2,
              y: node2.y + node2.height / 2
            };
            const angle = Math.atan2(center2.y - center1.y, center2.x - center1.x);
            const moveDistance = SPACING;
            node1.node.setPosition(
              node1.x - Math.cos(angle) * moveDistance / 2,
              node1.y - Math.sin(angle) * moveDistance / 2
            );
            node2.node.setPosition(
              node2.x + Math.cos(angle) * moveDistance / 2,
              node2.y + Math.sin(angle) * moveDistance / 2
            );
            node1.x = node1.node.getPosition().x;
            node1.y = node1.node.getPosition().y;
            node2.x = node2.node.getPosition().x;
            node2.y = node2.node.getPosition().y;
          }
        }
      }
    }
  
    engine.repaintCanvas();
  }, [model, engine]);

  useEffect(() => {
    const handleNodeDragComplete = () => {
      delapNodes()
    };
    window.addEventListener('nodeDragComplete', handleNodeDragComplete);
    return () => {
      window.removeEventListener('nodeDragComplete', handleNodeDragComplete)
    };
  }, [engine, delapNodes])

  const checkSavedCanvasPosition = useCallback(async () => {
    try {
      const result = await DashboardService.getSavedCanvasData();
      if (result.data && result.data.savedCanvasPosition) {
        localStorage.setItem('hasSavedCanvasPosition', 'true');
      } else {
        localStorage.removeItem('hasSavedCanvasPosition');
      }
    } catch (error) {
      console.error('Error checking saved canvas position:', error);
      localStorage.removeItem('hasSavedCanvasPosition');
    }
  }, []);

  useEffect(() => {
    checkSavedCanvasPosition();
  }, [checkSavedCanvasPosition]);

  const gridLayout = useCallback(async () => {
    const nodeListCheck = model.getNodes();
    if (nodeListCheck.length === 1) {
      const node = nodeListCheck[0];
      if (!(`selectedNe` in node)) {
        toasterError("No Nodes Selected");
        return;
      }
    }
    const checkAndSaveCanvasPosition = async () => {
      try {
        const result = await DashboardService.getSavedCanvasData();
        if (result.data && result.data.savedCanvasPosition) {
          /*const userConfrim = window.confirm ('Would you like to save your current layout before applying grid layout?');
          if (userConfrim) {
          await updateCanvasDataToAPI(true);
          }*/
          const userChoice = await promisedAreYouSure("Would you like to save your current canvas before applying map layout?")
          switch (userChoice) {
            case UserChoice.cancelFunction:
              return false;
            case UserChoice.saveBeforeApplyingFunction:
              await updateCanvasDataToAPI(true);
              return true;
            case UserChoice.dontSaveBeforeApplyingFunction:
              return true;  
          }
        } else {
          await updateCanvasDataToAPI(true);
          return true; 
        }
      } catch (error) {
        console.error('Error checking saved canvas position:', error);
      }
    };
    
    if(!await checkAndSaveCanvasPosition()) { return; }
    
    //setCanvasData(selectedNetworkElements);
    const nodeListX = model.getNodes();
    const nodeListY = model.getNodes();

    // Finding object with minimum x position
    let objectWithMinX = nodeListX.reduce((minXObject, currentNode) => {
      if (currentNode.getX() < minXObject.getX()) {
        return currentNode;
      } else {
        return minXObject;
      }
    });

    // if (objectWithMinX) {
    //   console.log("MinX:", objectWithMinX.getX()); // Accessing the x position if objectWithMinX is not null
    // } else {
    //   console.log("No object found in nodeListX"); // Handle case where nodeListX is empty
    // }
    
    // Finding object with minimum y position
    let objectWithMinY = nodeListY.reduce((minYObject, currentNode) => {
      if (currentNode.getY() < minYObject.getY()) {
        return currentNode;
      } else {
        return minYObject;
      }
    });

    // if (objectWithMinY) {
    //   console.log("MinY:", objectWithMinY.getY()); // Accessing the y position if objectWithMinY is not null
    // } else {
    //   console.log("No object found in nodeListY"); // Handle case where nodeListY is empty
    // }
    
    // checkAndSaveCanvasPosition().then(() => {
    //this gets the canvas size in pixels and then adds padding
    const canvas = engine.getCanvas();
    const boundingRectGrid = canvas.getBoundingClientRect();
    const padding = 20;
    const extraHeightNode = 82;
    
    const canvasWidthGrid = (boundingRectGrid.width - padding) / (400 );
    const canvasHeightGrid = (boundingRectGrid.height - padding) / (400 + extraHeightNode);
    
    const nodeListGrid = model.getNodes();
    const getGeographicalCoords = (node: any) => {
      try {
        const id = Number(node.selectedNe.id);
        const networkElement = selectedNetworkElements.find((ele: any) => ele.id === id);
        return { longitude: networkElement.longitude, latitude: networkElement.latitude };
      } catch {
        return { longitude: 0, latitude: 0 };
      }
    };
    
    let modifiedNodeListGrid = nodeListGrid.map((ele: any) => {
      const { longitude, latitude } = getGeographicalCoords(ele);
      //longitude and latitude were strings
      const floatLongitude = parseFloat(longitude);
      const floatLatitude = parseFloat(latitude);
      return { node: ele, longitude: floatLongitude, latitude: floatLatitude };
    });
    //const totalColumns = Math.ceil(Math.sqrt(nodeList.length));
    
    const totalColumns = Math.floor(Math.sqrt(nodeListGrid.length / (canvasWidthGrid * canvasHeightGrid )) * canvasWidthGrid) +1;
    const totalRows = Math.ceil(nodeListGrid.length / (totalColumns+1));
    let x = padding,
      y = padding;
    let rightEdge = 0,
      bottomEdge = 0;  
    let numColumns = 0;
    let maxHeight = 0;
    
    modifiedNodeListGrid.sort((a, b) => {
      return b.latitude - a.latitude;
    });
    
    const n = totalColumns + 1; // Number of elements per group
    for (let i = 0; i < nodeListGrid.length; i += n) {
      // Slice out the current group of n elements
      let group = modifiedNodeListGrid.slice(i, i + n);
      // Sort this group by longitude (ascending order)
      group.sort((a, b) => a.longitude - b.longitude);
      // Replace the sorted group back into the original array
      modifiedNodeListGrid.splice(i, n, ...group);
    }
    
    modifiedNodeListGrid.forEach((item) => {
      const { node, longitude, latitude } = item ;
      node.updateDimensions({ width: 300, height: 300 });
      const nodeRightEdge = x + node.width + padding;
      const nodeBottomEdge = y + node.height + 41 + padding;
      rightEdge = Math.max(nodeRightEdge, rightEdge);
      bottomEdge = Math.max(nodeBottomEdge, bottomEdge);
      node.setPosition(x, y);
      
      if (numColumns < totalColumns - 1) {
        maxHeight = Math.max(maxHeight, node.height);
        x += node.width + padding;
        numColumns++;
      } else {
        numColumns = 0;
        y += maxHeight + padding + extraHeightNode;
        x = padding;
        maxHeight = 0;
      };
    });
    engine.repaintCanvas();
    //delapNodes();
    engine.zoomToFit();
    // ZoomToFit
    const zoomLevelHeightGrid = (boundingRectGrid.height - (padding) ) / (bottomEdge);
    const zoomLevelWidthGrid = (boundingRectGrid.width - (padding) ) / (rightEdge);
    const zoomLevelGrid = Math.min(zoomLevelHeightGrid,zoomLevelWidthGrid,.7);
    model.setZoomLevel(zoomLevelGrid * 100);
    model.setOffset(model.getOffsetX() + 0, model.getOffsetY() + 41 * zoomLevelGrid);
    // FitToZoom
    // const zoomLevelGrid = model.getZoomLevel() / 100;
    // const zoomLevelHeightGrid = (boundingRectGrid.height ) / (bottomEdge);
    // const zoomLevelWidthGrid = (boundingRectGrid.width ) / (rightEdge);
    // modifiedNodeListGrid.forEach((item) => {
    //  const { node } = item;
    //  let x = Math.max(node.getX() - objectWithMinX.getX(),padding) ;
    //  let y = Math.max(node.getY() - objectWithMinX.getY(),padding) ;
    //  node.setPosition(x * (boundingRectGrid.width / zoomLevelGrid) / rightEdge, y * (boundingRectGrid.height/ zoomLevelGrid) / bottomEdge);
    // });
    //model.setOffset(model.getOffsetX() + 0, model.getOffsetY() + 41 * zoomLevelGrid);
    //engine.zoomToFitNodes({margin: 20});
    engine.repaintCanvas();
    // });
    }, [model, selectedNetworkElements]);

  const geographicalLayout = useCallback(async () => {
    const checkAndSaveCanvasPosition = async () => {
      try {
        const result = await DashboardService.getSavedCanvasData();
        if (result.data && result.data.savedCanvasPosition) {
          /*const userConfrim = window.confirm ('Would you like to save your current layout before applying grid layout?');
          if (userConfrim) {
          await updateCanvasDataToAPI(true);
          }*/
          const userChoice = await promisedAreYouSure("Would you like to save your current canvas before applying map layout?")
          switch (userChoice) {
            case UserChoice.cancelFunction:
              return false;
            case UserChoice.saveBeforeApplyingFunction:
              await updateCanvasDataToAPI(true);
              return true;
            case UserChoice.dontSaveBeforeApplyingFunction:
              return true;  
          }
        } else {
          await updateCanvasDataToAPI(true);
          return true; 
        }
      } catch (error) {
        console.error('Error checking saved canvas position:', error);
      }
    };
    
    if(!await checkAndSaveCanvasPosition()) { return; }
    
    const nodeList = model.getNodes();
    const getGeographicalCoords = (node: any) => {
      try {
        const id = Number(node.selectedNe.id);
        const networkElement = selectedNetworkElements.find((ele: any) => ele.id === id);
        return { longitude: networkElement.longitude, latitude: networkElement.latitude };
      } catch {
        return { longitude: 0, latitude: 0 };
      }
    };

    const userZoomLevel = model.getZoomLevel();
    //zoom is just a percentage. It makes math clearer to call it PixelsPerCoord when converting. e.g., Coord * Pixel/Coord = Pixel. 
    const PixelsPerCoord = userZoomLevel / 100;
    const modifiedNodeList = nodeList.map((ele: any) => {
    const { longitude, latitude } = getGeographicalCoords(ele);
    //longitude and latitude were strings
    const floatLongitude = parseFloat(longitude);
    const floatLatitude = parseFloat(latitude);
    return { node: ele, longitude: floatLongitude, latitude: floatLatitude };
    });
    
    //This is not sorting in place - use a max/min function instead. This needs to be redone to accomodate nodes near Prime Meridian.
    //modifiedNodeList.sort((a, b) => {
    //  return a.longitude - b.longitude;
    //});
    //console.log("nodeListLong", modifiedNodeList);
    
    let objectWithMaxLongitude = modifiedNodeList.reduce((maxObject, currentObject) => { 
      return currentObject.longitude > maxObject.longitude ? currentObject : maxObject;
    });
    let objectWithMinLongitude = modifiedNodeList.reduce((minObject, currentObject) => {
      return currentObject.longitude < minObject.longitude ? currentObject : minObject;
    });

    const findLongDiffDegrees = objectWithMaxLongitude.longitude - objectWithMinLongitude.longitude;
    const findLongCenterDegrees = objectWithMinLongitude.longitude + findLongDiffDegrees / 2;
    //Space on right side of canvas depends on the width of the right most object. Width is returned in coodinates    
    const eastNodeWidthPixels = objectWithMaxLongitude.node.width * PixelsPerCoord;
    //console.log("East>West Degrees:",  objectWithMinLongitude.longitude, findLongCenterDegrees, objectWithMaxLongitude.longitude);
    
    let objectWithMaxLatitude = modifiedNodeList.reduce((maxObject, currentObject) => {
      return currentObject.latitude > maxObject.latitude ? currentObject : maxObject;
    });
    let objectWithMinLatitude = modifiedNodeList.reduce((minObject, currentObject) => {
      return currentObject.latitude < minObject.latitude ? currentObject : minObject;
    });

    const findLatDiffDegrees = objectWithMaxLatitude.latitude - objectWithMinLatitude.latitude;
    const findLatCenterDegrees = objectWithMinLatitude.latitude + findLatDiffDegrees / 2;
    //Space on bottom side of canvas depends on the height of the bottom most object. Width is returned in coodinates.    
    const southNodeHeightPixels = objectWithMinLatitude.node.height * PixelsPerCoord;
    //console.log("North>South:", objectWithMinLatitude.latitude, findLatCenterDegrees, objectWithMaxLatitude.latitude);
      
    const canvas = engine.getCanvas();
    const boundingRect = canvas.getBoundingClientRect();
    //console.log("Canvas width pixels:", boundingRect.width);
    //console.log("Canvas height pixels:", boundingRect.height);
    //console.log("Zoom (Coords per Pixel)", PixelsPerCoord);
    
    //The height nodes doesn include the header and footer. Those vary with zoom level, so padding includes a pixel component + a variable coord component. 
    const topPaddingPixels = 20 + 50 * PixelsPerCoord;
    const bottomPaddingPixels = 20 + 50 * PixelsPerCoord;
    const leftPaddingPixels = 20;
    const rightPaddingPixels = 30;
    
    //this gets the canvas size in pixels and then adds padding
    const canvasWidthPixels = boundingRect.width - Number(eastNodeWidthPixels) - leftPaddingPixels - rightPaddingPixels;
    const canvasHeightPixels = boundingRect.height - Number(southNodeHeightPixels) - topPaddingPixels - bottomPaddingPixels;
    const canvasXcenterPixels = leftPaddingPixels + (canvasWidthPixels / 2);
    const canvasYcenterPixels = topPaddingPixels + (canvasHeightPixels / 2);
    //choose whether there are more degrees per pixel in the E>W or N>S direction
    const DegreesPerPixel = Math.max(findLongDiffDegrees / canvasWidthPixels, findLatDiffDegrees / canvasHeightPixels );
    //console.log("canvasSizePixels:", canvasWidthPixels , canvasHeightPixels);
    //console.log("canvasCenterPixels:", canvasXcenterPixels, canvasYcenterPixels);
    //console.log("DegreesPerPixel:", DegreesPerPixel);
    
    modifiedNodeList.forEach((item, index) => {
      const { node, longitude, latitude } = item;
      if (index === -1) return;
  
      //Position nodes by coordinates. This finds offset from center in Pixels then converts.
      const offsetXPixels = -(findLongCenterDegrees - modifiedNodeList[index].longitude ) / DegreesPerPixel;
      const offsetYPixels = (findLatCenterDegrees - modifiedNodeList[index].latitude ) / DegreesPerPixel;
      const PositionXCoords = (canvasXcenterPixels + offsetXPixels) / PixelsPerCoord;
      const PositionYCoords = (canvasYcenterPixels + offsetYPixels) / PixelsPerCoord;
      node.setPosition(PositionXCoords, PositionYCoords);  
      //console.log("nodeXY", node);
      //console.log("offset", findLongCenterDegrees, modifiedNodeList[index].longitude, offsetXPixels, findLatCenterDegrees, offsetYPixels);
      //console.log("X", canvasXcenterPixels / userZoomLevel, offsetXPixels / userZoomLevel, PositionXCoords);
      //console.log("Y", canvasYcenterPixels / userZoomLevel, offsetYPixels / userZoomLevel, PositionYCoords);
      
    });
  
    engine.zoomToFit();
  
    const nodeCount = modifiedNodeList.length;
    const nodeExponent = 3;
    const nodeNumerator = 350000;
    const degreeExponent = 0.3;
    const degreeNumerator = 3.5;
    
    const nodeZoom = nodeNumerator / ( (nodeCount+20) ** nodeExponent );
    const degreeZoom = degreeNumerator / ( DegreesPerPixel ** degreeExponent );
    const ZoomFitLevel = Math.min(Math.max(nodeZoom + degreeZoom, 10), 70);
          
    model.setZoomLevel(ZoomFitLevel);
    delapNodes();
    engine.repaintCanvas();
  }, [model, selectedNetworkElements]);
  
    
  //   const geographicalLayout = useCallback(async () => {
  //   const nodeListCheck = model.getNodes();
  //   if (nodeListCheck.length === 1) {
  //     const node = nodeListCheck[0];
  //     if (!(`selectedNe` in node)) {
  //       toasterError("No Nodes Selected");
  //       return;
  //     }
  //   }
  //   const checkAndSaveCanvasPosition = async () => {
  //     try {
  //       const result = await DashboardService.getSavedCanvasData();
  //       if (result.data && result.data.savedCanvasPosition) {
  //         /*const userConfrim = window.confirm ('Would you like to save your current layout before applying grid layout?');
  //         if (userConfrim) {
  //         await updateCanvasDataToAPI(true);
  //         }*/
  //         const userChoice = await promisedAreYouSure("Would you like to save your current canvas before applying map layout?")
  //         switch (userChoice) {
  //           case UserChoice.cancelFunction:
  //             return false;
  //           case UserChoice.saveBeforeApplyingFunction:
  //             await updateCanvasDataToAPI(true);
  //             return true;
  //           case UserChoice.dontSaveBeforeApplyingFunction:
  //             return true;  
  //         }
  //       } else {
  //         await updateCanvasDataToAPI(true);
  //       }
  //     } catch (error) {
  //       console.error('Error checking saved canvas position:', error);
  //     }
  //   };
    
  //   if(!await checkAndSaveCanvasPosition()) { return; }

  //   const nodeList = model.getNodes(); 
  //   const getGeographicalCoords = (node: any) => {
  //     try {
  //       const id = Number(node.selectedNe.id);
  //       const networkElement = selectedNetworkElements.find((ele: any) => ele.id === id);
  //       //console.log(networkElement.getPosition());
  //       return { longitude: networkElement.longitude, latitude: networkElement.latitude };
  //     } catch {
  //       return { longitude: 0, latitude: 0 };
  //     }
  //   };

  //   const userZoomLevel = model.getZoomLevel();
  //   //zoom is just a percentage. It makes math clearer to call it PixelsPerCoord when converting. e.g., Coord * Pixel/Coord = Pixel.

  //   const PixelsPerCoord = userZoomLevel / 100;
  //   const modifiedNodeList = nodeList.map((ele: any) => {
  //     const { longitude, latitude } = getGeographicalCoords(ele);
  //     const floatLongitude = parseFloat(longitude);
  //     const floatLatitude = parseFloat(latitude);
  //     return { node: ele, longitude: floatLongitude, latitude: floatLatitude };
  //   });
   
  //   let objectWithMaxLongitude = modifiedNodeList.reduce((maxObject, currentObject) => {
  //     return currentObject.longitude > maxObject.longitude ? currentObject : maxObject;
  //   });
  //   let objectWithMinLongitude = modifiedNodeList.reduce((minObject, currentObject) => {
  //     return currentObject.longitude < minObject.longitude ? currentObject : minObject;
  //   });

  //   const findLongDiffDegrees = objectWithMaxLongitude.longitude - objectWithMinLongitude.longitude;
  //   const findLongCenterDegrees = objectWithMinLongitude.longitude + findLongDiffDegrees / 2;
  //   //Space on right side of canvas depends on the width of the right most object. Width is returned in coodinates    
  //   const eastNodeWidthPixels = objectWithMaxLongitude.node.width * PixelsPerCoord;
  //   //console.log("East>West Degrees:",  objectWithMinLongitude.longitude, findLongCenterDegrees, objectWithMaxLongitude.longitude);

  //   let objectWithMaxLatitude = modifiedNodeList.reduce((maxObject, currentObject) => {
  //     return currentObject.latitude > maxObject.latitude ? currentObject : maxObject;
  //   });
  //   let objectWithMinLatitude = modifiedNodeList.reduce((minObject, currentObject) => {
  //     return currentObject.latitude < minObject.latitude ? currentObject : minObject;
  //   });
  //   const findLatDiffDegrees = objectWithMaxLatitude.latitude - objectWithMinLatitude.latitude;
  //   const findLatCenterDegrees = objectWithMinLatitude.latitude + findLatDiffDegrees / 2;
  //   //Space on bottom side of canvas depends on the height of the bottom most object. Width is returned in coodinates.    
  //   const southNodeHeightPixels = objectWithMinLatitude.node.height * PixelsPerCoord;
  //   //console.log("North>South:", objectWithMinLatitude.latitude, findLatCenterDegrees, objectWithMaxLatitude.latitude).
     
  //   const canvas = engine.getCanvas();
  //   const boundingRect = canvas.getBoundingClientRect();
  //   //console.log("Canvas width pixels:", boundingRect.width);
  //   //console.log("Canvas height pixels:", boundingRect.height);
  //   //console.log("Zoom (Coords per Pixel)", PixelsPerCoord);
   
  //   //The height nodes doesn include the header and footer. Those vary with zoom level, so padding includes a pixel component + a variable coord component.
  //   const topPaddingPixels = 20 + 50 * PixelsPerCoord;
  //   const bottomPaddingPixels = 20 + 50 * PixelsPerCoord;
  //   const leftPaddingPixels = 20;
  //   const rightPaddingPixels = 30;

  //   //this gets the canvas size in pixels and then adds padding
  //   const canvasWidthPixels = boundingRect.width - Number(eastNodeWidthPixels) - leftPaddingPixels - rightPaddingPixels;
  //   const canvasHeightPixels = boundingRect.height - Number(southNodeHeightPixels) - topPaddingPixels - bottomPaddingPixels;
  //   const canvasXcenterPixels = leftPaddingPixels + (canvasWidthPixels / 2);
  //   const canvasYcenterPixels = topPaddingPixels + (canvasHeightPixels / 2);
  //   //choose whether there are more degrees per pixel in the E>W or N>S direction
  //   const DegreesPerPixel = Math.max(findLongDiffDegrees / canvasWidthPixels, findLatDiffDegrees / canvasHeightPixels );
  //   //console.log("canvasSizePixels:", canvasWidthPixels , canvasHeightPixels);
  //   //console.log("canvasCenterPixels:", canvasXcenterPixels, canvasYcenterPixels);
  //   //console.log("DegreesPerPixel:", DegreesPerPixel);
  //   // checkAndSaveCanvasPosition().then(() => {
  //   modifiedNodeList.forEach((item, index) => {
  //     const { node, longitude, latitude } = item;
  //     if (index === -1) return;

  //     //Position nodes by coordinates. This finds offset from center in Pixels then converts.
  //     const offsetXPixels = -(findLongCenterDegrees - modifiedNodeList[index].longitude ) / DegreesPerPixel;
  //     const offsetYPixels = (findLatCenterDegrees - modifiedNodeList[index].latitude ) / DegreesPerPixel;
  //     const PositionXCoords = (canvasXcenterPixels + offsetXPixels) / PixelsPerCoord;
  //     const PositionYCoords = (canvasYcenterPixels + offsetYPixels) / PixelsPerCoord;
  //     node.setPosition(PositionXCoords, PositionYCoords);  
  //     //console.log("nodeXY", node);
  //     //console.log("offset", findLongCenterDegrees, modifiedNodeList[index].longitude, offsetXPixels, findLatCenterDegrees, offsetYPixels);
  //     //console.log("X", canvasXcenterPixels / userZoomLevel, offsetXPixels / userZoomLevel, PositionXCoords);
  //     //console.log("Y", canvasYcenterPixels / userZoomLevel, offsetYPixels / userZoomLevel, PositionYCoords);
  //   }); 
      
  //   engine.zoomToFit();
  //   engine.repaintCanvas();
  // // });
  // }, [model, selectedNetworkElements]);

  /*const recenter = useCallback(() => {
    engine.zoomToFitNodes({margin: 100});
    engine.repaintCanvas();
  }, [model]);*/

  useEffect(() => {
    PortsUpdated();
    setPortLinks();
  }, [ports, canvasData, selectedNetworkElements]);

  const updateScale = (step: number) => {
    ZoomFunction(engine, null, step);
  };

  const restoreLayout = useCallback(() => {
    DashboardService.getSavedCanvasData()
      .then(async result => {
        await updateCanvasDataToAPI();
        const savedCanvasPosition = result.data.savedCanvasPosition;
        if (savedCanvasPosition) {
          setCanvasData(savedCanvasPosition);
          const nodeList = model.getNodes();
          nodeList.forEach(node => {
            const savedNode = savedCanvasPosition.nodes.find((savedNode: { id: string; }) => savedNode.id === node.getID());
            if (savedNode) {
              node.setPosition(savedNode.position.x, savedNode.position.y);
            }
          });
          model.setZoomLevel(savedCanvasPosition.zoom);
          model.setOffset(savedCanvasPosition.offsetX, savedCanvasPosition.offsetY);
          engine.repaintCanvas();
  
          DashboardService.clearSavedCanvasData()
            .then(() => {
              checkSavedCanvasPosition();
            })
            .catch(error => {
              console.error('Error clearing saved canvas position:', error);
            });
        } else {
          console.log('No saved canvas position found.');
        }
      })
      .catch(error => {
        console.error('Error loading saved canvas position:', error);
      });
      //engine.zoomToFit();
  }, [model]);

  const printZoom = useCallback(() => {
    const userZoomLevel = model.getZoomLevel();
  }, [model]); // Add model as a dependency

  const fitToZoom = useCallback(() => {
    const nodeList = model.getNodes();
    const userZoomLevel = model.getZoomLevel();
    const canvas =  engine.getCanvas();
    const boundingRect = canvas.getBoundingClientRect();
    const pixelsPerCoord = userZoomLevel / 100;

    const listWithNodeCoordinates = nodeList.map((ele: any) => {
      const floatPositionX = parseFloat(ele.position.x);
      const floatPositionY = parseFloat(ele.position.y);
      return { node: ele, x: floatPositionX, y: floatPositionY };
    });

    let nodeWithMaxX = listWithNodeCoordinates.reduce((maxX, currentX) => {
      return currentX.x > maxX.x ? currentX : maxX;
    });
    let nodeWithMinX = listWithNodeCoordinates.reduce((minX, currentX) => {
      return currentX.x < minX.x ? currentX : minX;
    });

    const findXDiffDegrees = nodeWithMaxX.x - nodeWithMinX.x;
    const findXCenterDegrees = nodeWithMinX.x + findXDiffDegrees / 2;
    const rightNodeWidthPixels = nodeWithMaxX.node.width * pixelsPerCoord;

    let nodeWithMaxY = listWithNodeCoordinates.reduce((maxY, currentY) => {
      return currentY.y > maxY.y ? currentY : maxY;
    });
    let nodeWithMinY = listWithNodeCoordinates.reduce((minY, currentY) => {
      return currentY.y < minY.y ? currentY : minY;
    });

    const findYDiffDegrees = nodeWithMaxY.y - nodeWithMinY.y;
    const findYCenterDegrees = nodeWithMinY.y + findYDiffDegrees / 2;
    const bottomNodeHeightPixels = nodeWithMinY.node.width * pixelsPerCoord; 

    const topPaddingPixels = 20 + 50 * pixelsPerCoord;
    const leftPaddingPixels = 20;

    const canvasWidthPixels = boundingRect.width - Number(rightNodeWidthPixels) - leftPaddingPixels - rightNodeWidthPixels;
    const canvasHeightPixels = boundingRect.height - Number(bottomNodeHeightPixels) - topPaddingPixels - rightNodeWidthPixels;
    const canvasXcenterPixels = leftPaddingPixels + (canvasWidthPixels / 2);
    const canvasYcenterPixels = topPaddingPixels + (canvasHeightPixels / 2);
    const DegreesPerPixel = Math.max(findXDiffDegrees / canvasWidthPixels, findYDiffDegrees / canvasHeightPixels);

    listWithNodeCoordinates.forEach((item, index) => {
      const { node } = item;
      if (index === -1) return;

      const offsetXPixels = -(findXCenterDegrees - listWithNodeCoordinates[index].x) / DegreesPerPixel;
      const offsetYPixels = -(findYCenterDegrees - listWithNodeCoordinates[index].y) / DegreesPerPixel;
      const PositionXCoords = (canvasXcenterPixels + offsetXPixels) / pixelsPerCoord;
      const PositionYCoords = (canvasYcenterPixels + offsetYPixels) / pixelsPerCoord;
      node.setPosition(PositionXCoords, PositionYCoords);
    });

    engine.zoomToFit();
    engine.repaintCanvas();
  }, [model, selectedNetworkElements]);

  const zoomToFit = useCallback(() => {
    delapNodes()
    const nodeList = model.getNodes();
    engine.zoomToFitNodes({margin:0, nodes:nodeList, maxZoom: 70});
  }, [engine, selectedNetworkElements]);

  useEffect(() => {
    const handleDisableZoom = () => {
      setIsZoomDisabled(true);
    };
    window.addEventListener('disableZoom', handleDisableZoom);
    return () => {
      window.removeEventListener('disableZoom', handleDisableZoom);
    };
  }, []); 
  
  useEffect(() => {
    const handleEnableZoom = () => {
      setIsZoomDisabled(false);
    };
    window.addEventListener('enableZoom', handleEnableZoom);
    return () => {
      window.removeEventListener('enableZoom', handleEnableZoom);
    };
  }, []);

  return (
    <>
      {!isZoomDisbaled && <MenuSection
      // handleSaveReset={menuProps.handleSaveReset}
      // isDirty={menuProps.isDirty}
      // recenter={recenter}
      zoomToFit={zoomToFit}
      fitToZoom={fitToZoom}
      printZoom={printZoom}
      saveLayout={() => updateCanvasDataToAPI(true)}
      restoreLayout={restoreLayout}
      gridLayout={gridLayout}
      geoLayout={geographicalLayout}
      engine={engine}
      />}
      {dirtyData.length > 0 && (
        <div
          style={{ position: "absolute", bottom: 20, right: "calc(50% - 150px)", zIndex: 100, display: "flex", justifyContent: "space-between", width: 300 }}
        >
          <Button
            className="save-reset-button"
            disabled={dirtyData.length <= 0}
            variant="success"
            onClick={() => {
              handleSave();
            }}
            text="Save"
          />
          <Button
            className="save-reset-button"
            disabled={dirtyData.length <= 0}
            variant="close"
            onClick={() => {
              setDirtyData([]);
              refetchAll();
            }}
            text="Reset"
          />
        </div>
      )}
      <div className="canvas-root">
        <div className="canvas-with-spinner" ref={ref}>
          {showData && <CanvasBoard engine={engine} />}
          {(portsLoading || linksLoading) && <Spinner className="loading-spinner" size={30} />}
        </div>
        {!isZoomDisbaled && <CanvasController onRangeChange={updateScale}/>}
      </div>
    </>
  );
};

export default Canvas;
